import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEQUENTIAL, sequentialStep } from './palette';

export interface CelulaHeatmap {
  data: string;
  dia: number;
  horas: number;
  horasLabel: string;
  fimDeSemana: boolean;
  fechada: boolean;
}

export interface LinhaHeatmap {
  id: string;
  nome: string;
  celulas: CelulaHeatmap[];
}

/**
 * Completeness at a glance: one row per eletricista, one cell per day, shaded on
 * a single-hue sequential ramp. A weekday with no hours is left as the empty
 * step with a dashed edge — "não entregou" has to be visible without counting.
 */
export function HeatmapEntregas({ linhas, max }: { linhas: LinhaHeatmap[]; max: number }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<{ nome: string; celula: CelulaHeatmap } | null>(null);

  return (
    <div>
      <div className="flex flex-col gap-1.5">
        {linhas.map((linha) => (
          <div key={linha.id} className="flex items-center gap-2">
            <span className="w-16 shrink-0 truncate text-[10px] text-[#5c6373] md:w-24 md:text-[11px]">
              {linha.nome}
            </span>
            <div className="flex flex-1 gap-[2px]">
              {linha.celulas.map((celula) => {
                const vazioUtil = celula.horas === 0 && !celula.fimDeSemana;
                return (
                  <button
                    key={celula.data}
                    type="button"
                    aria-label={`${linha.nome} · ${celula.data}: ${
                      celula.horas > 0 ? celula.horasLabel : t('graficos.semParte')
                    }`}
                    onMouseEnter={() => setHover({ nome: linha.nome, celula })}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover({ nome: linha.nome, celula })}
                    onBlur={() => setHover(null)}
                    className={`h-5 flex-1 rounded-[3px] focus:outline-none focus-visible:ring-2 md:h-6
                                focus-visible:ring-conduril/40 ${
                                  vazioUtil ? 'border border-dashed border-[#d6c39a]' : ''
                                }`}
                    style={{
                      background: celula.fimDeSemana
                        ? '#f6f7fa'
                        : sequentialStep(celula.horas, max),
                    }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="h-4 text-[10px] leading-4 text-[#5c6373]">
          {hover ? (
            <>
              {hover.nome} · {hover.celula.dia} ·{' '}
              <span className="num font-semibold">
                {hover.celula.horas > 0 ? hover.celula.horasLabel : t('graficos.semParte')}
              </span>
            </>
          ) : (
            <span className="text-conduril-muted">{t('graficos.completudeLegenda')}</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-[9px] text-conduril-muted">0</span>
          {SEQUENTIAL.slice(1).map((color) => (
            <span key={color} className="h-2.5 w-3.5 rounded-[2px]" style={{ background: color }} />
          ))}
          <span className="num text-[9px] text-conduril-muted">{max}h</span>
        </div>
      </div>
    </div>
  );
}
